<template>
  <section class="ad-options-section">
    <div class="section-header">
      <h2 class="section-title">🚀 광고 옵션</h2>
      <p class="section-desc">공고를 더 많은 사람에게 노출하세요</p>
    </div>

    <div class="points-info">
      <span class="label">보유 포인트</span>
      <span class="value">{{ formatPrice(userPoints) }}P</span>
    </div>

    <!-- 공고 업그레이드 -->
    <div class="option-group">
      <h3>📌 공고 업그레이드</h3>
      <div class="option-list">
        <label 
          v-for="product in jobProducts" 
          :key="product.id" 
          class="option-item"
          :class="{ selected: isSelected(product.id), disabled: product.price > userPoints && !isSelected(product.id) }"
        >
          <input 
            type="checkbox" 
            :value="product.id" 
            v-model="selectedProducts"
            :disabled="product.price > userPoints && !isSelected(product.id)"
          />
          <div class="option-icon" :style="{ background: product.color + '15', color: product.color }">
            {{ product.icon }}
          </div>
          <div class="option-info">
            <span class="option-name">{{ product.name }}</span>
            <span class="option-desc">{{ product.description }}</span>
          </div>
          <div class="option-price">
            <span class="price">{{ formatPrice(product.price) }}P</span>
            <span class="duration">/ {{ product.duration }}일</span>
          </div>
          <div class="check-icon" v-if="isSelected(product.id)">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
            </svg>
          </div>
        </label>
      </div>
    </div>

    <!-- 배너 광고 -->
    <div class="option-group">
      <h3>🖼️ 배너 광고 (등록 후 추가 설정)</h3>
      <div class="option-list">
        <label 
          v-for="product in bannerProducts" 
          :key="product.id" 
          class="option-item"
          :class="{ selected: isSelected(product.id), disabled: product.price > userPoints && !isSelected(product.id) }"
        >
          <input 
            type="checkbox" 
            :value="product.id" 
            v-model="selectedProducts"
            :disabled="product.price > userPoints && !isSelected(product.id)"
          />
          <div class="option-icon" :style="{ background: product.color + '15', color: product.color }">
            {{ product.icon }}
          </div>
          <div class="option-info">
            <span class="option-name">{{ product.name }}</span>
            <span class="option-desc">{{ product.description }}</span>
          </div>
          <div class="option-price">
            <span class="price">{{ formatPrice(product.price) }}P</span>
            <span class="duration">/ {{ product.duration }}일</span>
          </div>
          <div class="check-icon" v-if="isSelected(product.id)">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
            </svg>
          </div>
        </label>
      </div>
    </div>

    <!-- 결제 요약 -->
    <div v-if="selectedProducts.length > 0" class="payment-summary">
      <div class="summary-header">
        <h4>선택한 옵션</h4>
      </div>
      <div class="summary-list">
        <div v-for="productId in selectedProducts" :key="productId" class="summary-item">
          <span>{{ getProductName(productId) }}</span>
          <span class="item-price">{{ formatPrice(getProductPrice(productId)) }}P</span>
        </div>
      </div>
      <div class="summary-total">
        <span>총 결제 금액</span>
        <span class="total-price">{{ formatPrice(totalPrice) }}P</span>
      </div>
      <div v-if="totalPrice > userPoints" class="insufficient-warning">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
        </svg>
        <span>포인트가 {{ formatPrice(totalPrice - userPoints) }}P 부족합니다</span>
        <router-link to="/mypage/points" class="charge-link">충전하기</router-link>
      </div>
    </div>
  </section>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useProducts } from '@/composables/useProducts'
import { useAuth } from '@/composables/useAuth'

export default {
  name: 'JobAdOptionsSection',
  props: {
    modelValue: {
      type: Array,
      default: () => []
    }
  },
  emits: ['update:modelValue', 'total-change'],
  setup(props, { emit }) {
    const { products, loadProducts, getProduct, formatPrice } = useProducts()
    const { userProfile } = useAuth()

    const selectedProducts = ref([...props.modelValue])

    // 보유 포인트
    const userPoints = computed(() => userProfile.value?.points || 0)

    // 공고 업그레이드 상품
    const jobProducts = computed(() => 
      products.value.filter(p => p.category === 'job' && p.isActive).sort((a, b) => a.order - b.order)
    )

    // 배너 광고 상품
    const bannerProducts = computed(() => 
      products.value.filter(p => p.category === 'banner' && p.isActive).sort((a, b) => a.order - b.order)
    )

    // 총 가격
    const totalPrice = computed(() => {
      return selectedProducts.value.reduce((sum, id) => {
        const product = getProduct(id)
        return sum + (product?.price || 0)
      }, 0)
    })

    // 선택 여부 확인
    const isSelected = (productId) => selectedProducts.value.includes(productId)

    // 상품 정보 가져오기
    const getProductName = (productId) => getProduct(productId)?.name || ''
    const getProductPrice = (productId) => getProduct(productId)?.price || 0

    // 선택 변경 시 emit
    watch(selectedProducts, (newVal) => {
      emit('update:modelValue', newVal)
      emit('total-change', totalPrice.value)
    }, { deep: true })

    onMounted(async () => {
      await loadProducts()
    })

    return {
      selectedProducts,
      userPoints,
      jobProducts,
      bannerProducts,
      totalPrice,
      isSelected,
      getProductName,
      getProductPrice,
      formatPrice
    }
  }
}
</script>

<style scoped>
.ad-options-section {
  background: #fff;
  border: 1px solid #e5e8eb;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
}

.section-header {
  margin-bottom: 20px;
}

.section-title {
  font-size: 18px;
  font-weight: 700;
  color: #191f28;
  margin-bottom: 4px;
}

.section-desc {
  font-size: 14px;
  color: #6b7684;
}

.points-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f0f7ff;
  border-radius: 10px;
  margin-bottom: 24px;
}

.points-info .label {
  font-size: 14px;
  color: #6b7684;
}

.points-info .value {
  font-size: 18px;
  font-weight: 700;
  color: #3182f6;
}

/* 옵션 그룹 */
.option-group {
  margin-bottom: 24px;
}

.option-group h3 {
  font-size: 15px;
  font-weight: 600;
  color: #191f28;
  margin-bottom: 12px;
}

.option-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.option-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background: #f9fafb;
  border: 2px solid transparent;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.15s;
}

.option-item:hover:not(.disabled) {
  background: #f0f7ff;
  border-color: #3182f6;
}

.option-item.selected {
  background: #f0f7ff;
  border-color: #3182f6;
}

.option-item.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.option-item input {
  display: none;
}

.option-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.option-info {
  flex: 1;
  min-width: 0;
}

.option-name {
  display: block;
  font-size: 15px;
  font-weight: 600;
  color: #191f28;
  margin-bottom: 2px;
}

.option-desc {
  display: block;
  font-size: 13px;
  color: #6b7684;
}

.option-price {
  text-align: right;
  flex-shrink: 0;
}

.option-price .price {
  display: block;
  font-size: 16px;
  font-weight: 700;
  color: #3182f6;
}

.option-price .duration {
  font-size: 12px;
  color: #8b95a1;
}

.check-icon {
  width: 28px;
  height: 28px;
  background: #3182f6;
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* 결제 요약 */
.payment-summary {
  background: #f9fafb;
  border-radius: 12px;
  padding: 20px;
  margin-top: 24px;
}

.summary-header h4 {
  font-size: 15px;
  font-weight: 600;
  color: #191f28;
  margin-bottom: 12px;
}

.summary-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e8eb;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  color: #4e5968;
}

.item-price {
  font-weight: 500;
}

.summary-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.summary-total span:first-child {
  font-size: 15px;
  font-weight: 600;
  color: #191f28;
}

.total-price {
  font-size: 22px;
  font-weight: 700;
  color: #3182f6;
}

.insufficient-warning {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  padding: 12px;
  background: #fff5f5;
  border-radius: 8px;
  color: #ef4444;
  font-size: 13px;
}

.charge-link {
  margin-left: auto;
  padding: 6px 12px;
  background: #ef4444;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  border-radius: 6px;
  text-decoration: none;
}

.charge-link:hover {
  background: #dc2626;
}

/* 반응형 */
@media (max-width: 768px) {
  .ad-options-section {
    padding: 20px 16px;
  }

  .option-item {
    padding: 14px;
  }

  .option-icon {
    width: 40px;
    height: 40px;
    font-size: 18px;
  }

  .option-info {
    flex: 1;
  }

  .option-name {
    font-size: 14px;
  }

  .option-desc {
    font-size: 12px;
  }

  .option-price .price {
    font-size: 14px;
  }

  .check-icon {
    width: 24px;
    height: 24px;
  }

  .check-icon svg {
    width: 16px;
    height: 16px;
  }
}
</style>